import lodashGet from "lodash/get";
import { isNotNullOrUndefined } from "option-t/maybe";
import { expectNotNull } from "option-t/nullable";
import { createContext, type ReactNode, use, useMemo } from "react";
import type { Schema } from "type-fest";

/**
 * Extracts all placeholder values from a pattern string that contain placeholders
 * in the format `{Placeholder}`.
 *
 * @example
 * ```ts
 * type Test = ExtractPlaceholder<'hello {name}, welcome to {place}'>;
 * // Result: 'name' | 'place'
 * ```
 */
type ExtractPlaceholder<Pattern, Acc = never> =
  Pattern extends `${string}{${infer Placeholder}}${infer Rest}` ?
    ExtractPlaceholder<Rest, Placeholder | Acc>
  : Pattern extends `${string}{${infer Field}}` ? Field
  : Acc;

type PathImpl<T, Key extends keyof T> =
  Key extends string ?
    T[Key] extends Record<string, unknown> ?
      | `${Key}.${PathImpl<
          T[Key],
          Exclude<keyof T[Key], keyof Array<unknown>>
        > &
          string}`
      | `${Key}.${Exclude<keyof T[Key], keyof Array<unknown>> & string}`
    : never
  : never;
type PathImplHelper<T> = PathImpl<T, keyof T> | keyof T;

/**
 * Constructs a union type of all possible "dot-separated" paths within an object.
 *
 * @example
 * ```ts
 * type Example = {
 *   user: {
 *     name: string;
 *     address: {
 *       street: string;
 *       city: string;
 *     };
 *   };
 *   id: number;
 * };
 *
 * type ExamplePaths = Path<Example>;
 * // Result: "user" | "id" | "user.name" | "user.address" | "user.address.street" | "user.address.city"
 * ```
 */
type Path<T> =
  PathImplHelper<T> extends string | keyof T ? PathImplHelper<T> : keyof T;

type GetPathValue<T, P extends Path<T>> =
  P extends `${infer Key}.${infer Rest}` ?
    Key extends keyof T ?
      Rest extends Path<T[Key]> ?
        GetPathValue<T[Key], Rest>
      : never
    : never
  : P extends keyof T ? T[P]
  : never;

interface MessageObject {
  [key: string]: MessageObject | string;
}

/**
 * A utility type representing a function to format messages from a collection
 * of message objects.
 *
 * @Example:
 * ```ts
 * type Messages = {
 *   greeting: "Hello, {name}!";
 *   farewell: "Goodbye!";
 * };
 *
 * declare const formatMessage: FormatMessageFn<Messages>;
 *
 * // valid usage
 * formatMessage("greeting", { name: "John" });
 * formatMessage("farewell");
 * ```
 */
type FormatMessageFn<Messages extends MessageObject> = <
  Key extends Path<Messages>,
  PathValue = GetPathValue<Messages, Key>,
>(
  ...options: PathValue extends string ?
    ExtractPlaceholder<PathValue> extends never ?
      [key: Key]
    : [key: Key, args: Record<ExtractPlaceholder<PathValue>, string>]
  : never
) => string;

interface I18n<Messages extends MessageObject> {
  msg: FormatMessageFn<Messages>;
  effectiveLocale: Intl.Locale;
}

type ProvideMessagesFn<Messages extends MessageObject> = (
  locale: Intl.Locale,
) => Schema<
  // We are widening the leaf types to string since we are not interested in
  // the literal values (the actual pattern). The `MessageObject` leaf can be
  // narrowly type if the given `MessageObject` is a full literal
  // (defined with `as const`).
  Messages,
  string
> | null;

export const createI18n = <Messages extends MessageObject>(
  fallbackLocale: Intl.Locale,
  fallbackMessages: Messages,
) => {
  const I18nContext = createContext<I18n<Messages> | null>(null);

  const useI18n = () =>
    expectNotNull(use(I18nContext), "I18nContext should be initialised");

  const I18nProvider = (props: {
    languages: ReadonlyArray<
      Intl.UnicodeBCP47LocaleIdentifier | null | undefined
    >;
    provideMessages: ProvideMessagesFn<Messages>;
    children: ReactNode;
  }) => {
    const { languages, provideMessages, children } = props;

    // We minimise context changes to avoid rerender in the components using
    // the `useI18n` hook.
    const contextValue = useMemo(() => {
      let effectiveLocale = fallbackLocale;
      let effectiveMessages = fallbackMessages as MessageObject;

      // Build of preferred locales interleaved with their minified version so
      // that "en-GB, fr-FR" would produce "en-GB, en, fr-FR, fr" following the
      // mechanism defined in https://datatracker.ietf.org/doc/html/rfc4647#section-3.4
      const localesToTry = languages
        .filter(isNotNullOrUndefined)
        .flatMap((language) => {
          const locale = new Intl.Locale(language);
          return [locale, locale.minimize()];
        });

      for (const localeToTry of localesToTry) {
        if (localeToTry != null) {
          const messages = provideMessages(localeToTry);
          if (messages != null) {
            effectiveLocale = localeToTry;
            effectiveMessages = messages;
            break;
          }
        }
      }

      const formatMessage: FormatMessageFn<Messages> = (key, args?) => {
        let output = key as string;

        const pattern: unknown = lodashGet(effectiveMessages, key);
        if (typeof pattern === "string") {
          output = pattern;
          if (args != null) {
            for (const [placeholder, value] of Object.entries(args)) {
              if (typeof value === "string") {
                output = output.replaceAll(`{${placeholder}}`, value);
              }
            }
          }
        }

        return output;
      };

      return {
        msg: formatMessage,
        effectiveLocale,
      };
    }, [provideMessages, languages]);

    return <I18nContext value={contextValue}>{children}</I18nContext>;
  };

  return { I18nProvider, useI18n };
};
